/** El tablero de tareas.
 *
 * Se mueve con un desplegable en cada ficha, no arrastrando. Arrastrar es más vistoso pero
 * deja fuera a quien va por teclado o desde el móvil, y el tablero se usa tanto desde una cosa
 * como de la otra. Además el desplegable sólo ofrece los pasos que el servidor permite.
 */
import { useCallback, useEffect, useState } from 'react'

import { ErrorDeApi, api } from '../api'
import { Cargando, Dialogo, Error, EtiquetaDePrioridad, Vacio } from '../componentes/piezas'
import { usePortal } from '../contexto'
import { COLUMNAS, ESTADO_TAREA, PRIORIDAD, fecha } from '../textos'
import type { EstadoTarea, Miembro, Prioridad, Proyecto, Tarea } from '../tipos'

/** Adónde se puede mover desde cada columna. Es la misma tabla que aplica el servidor. */
const SALIDAS: Record<EstadoTarea, EstadoTarea[]> = {
  backlog: ['todo', 'in_progress', 'cancelled'],
  todo: ['backlog', 'in_progress', 'blocked', 'cancelled'],
  in_progress: ['todo', 'blocked', 'review', 'done', 'cancelled'],
  blocked: ['todo', 'in_progress', 'cancelled'],
  review: ['in_progress', 'done', 'cancelled'],
  done: ['in_progress'],
  cancelled: ['backlog'],
}

export default function Tablero() {
  const { ruta, avisar, puedo } = usePortal()
  const [tareas, setTareas] = useState<Tarea[] | null>(null)
  const [error, setError] = useState('')
  const [nueva, setNueva] = useState(false)
  const [soloMias, setSoloMias] = useState(false)
  const { yo } = usePortal()

  const cargar = useCallback(async () => {
    try {
      setTareas(await api.get<Tarea[]>(`${ruta}/tareas`))
      setError('')
    } catch (e) {
      setError((e as ErrorDeApi).message)
    }
  }, [ruta])

  useEffect(() => {
    void cargar()
  }, [cargar])

  async function mover(tarea: Tarea, estado: EstadoTarea) {
    try {
      await api.post(`${ruta}/tareas/${tarea.numero}/mover`, { estado })
      await cargar()
    } catch (e) {
      avisar((e as ErrorDeApi).message, 'malo')
    }
  }

  const visibles = (tareas ?? []).filter(
    (t) => !soloMias || t.responsable?.id === yo.persona.id,
  )

  return (
    <>
      <header className="cabecera">
        <h1>Tareas</h1>
        <div className="cabecera__acciones">
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, margin: 0 }}>
            <input
              type="checkbox" checked={soloMias} style={{ width: 'auto' }}
              onChange={(e) => setSoloMias(e.target.checked)}
            />
            Sólo las mías
          </label>
          {puedo('gestionar_tareas') && (
            <button className="boton boton--principal" onClick={() => setNueva(true)}>
              Nueva tarea
            </button>
          )}
        </div>
      </header>

      <div className="contenido">
        {error && <Error>{error}</Error>}
        {!tareas && !error && <Cargando filas={4} />}

        {tareas && tareas.length === 0 && (
          <div className="tarjeta">
            <Vacio titulo="El tablero está vacío">
              <p>
                Las tareas salen solas al convertir una solicitud aprobada en proyecto, o se
                crean a mano.
              </p>
            </Vacio>
          </div>
        )}

        {tareas && tareas.length > 0 && (
          <div className="tablero">
            {COLUMNAS.map((columna) => {
              const suyas = visibles.filter((t) => t.estado === columna)
              return (
                <section className={`columna columna--${columna}`} key={columna}>
                  <h2 className="columna__titulo">
                    {ESTADO_TAREA[columna]}
                    <span>{suyas.length}</span>
                  </h2>
                  {suyas.map((t) => (
                    <article
                      className={`ficha${t.vencida ? ' ficha--vencida' : ''}`}
                      key={t.numero}
                    >
                      <div className="ficha__titulo">{t.titulo}</div>
                      <div className="ficha__pie">
                        <span className="codigo">{t.codigo}</span>
                        <EtiquetaDePrioridad valor={t.prioridad} />
                      </div>
                      {t.responsable && (
                        <div className="fila__meta" style={{ marginTop: 5 }}>
                          {t.responsable.nombre}
                        </div>
                      )}
                      {t.vence && (
                        <div
                          className="fila__meta"
                          style={{ marginTop: 3, color: t.vencida ? 'var(--malo)' : undefined }}
                        >
                          {t.vencida ? 'venció' : 'vence'} {fecha(t.vence)}
                        </div>
                      )}
                      {t.puedo_moverla && SALIDAS[t.estado].length > 0 && (
                        <select
                          aria-label={`Mover ${t.codigo}`}
                          value=""
                          style={{ marginTop: 7, fontSize: '0.8rem', padding: '4px 6px' }}
                          onChange={(e) => {
                            if (e.target.value) void mover(t, e.target.value as EstadoTarea)
                          }}
                        >
                          <option value="">Mover a…</option>
                          {SALIDAS[t.estado].map((destino) => (
                            <option key={destino} value={destino}>{ESTADO_TAREA[destino]}</option>
                          ))}
                        </select>
                      )}
                    </article>
                  ))}
                  {suyas.length === 0 && (
                    <div style={{ color: 'var(--texto-tenue)', fontSize: '0.8rem', padding: 4 }}>
                      Nada aquí.
                    </div>
                  )}
                </section>
              )
            })}
          </div>
        )}
      </div>

      {nueva && (
        <NuevaTarea
          alCerrar={() => setNueva(false)}
          alCrear={() => {
            setNueva(false)
            void cargar()
          }}
        />
      )}
    </>
  )
}

function NuevaTarea({ alCerrar, alCrear }: { alCerrar: () => void; alCrear: () => void }) {
  const { ruta, avisar } = usePortal()
  const [titulo, setTitulo] = useState('')
  const [descripcion, setDescripcion] = useState('')
  const [prioridad, setPrioridad] = useState<Prioridad>('medium')
  const [responsable, setResponsable] = useState('')
  const [proyecto, setProyecto] = useState('')
  const [vence, setVence] = useState('')
  const [gente, setGente] = useState<Miembro[]>([])
  const [proyectos, setProyectos] = useState<Proyecto[]>([])
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)

  useEffect(() => {
    api.get<Miembro[]>(`${ruta}/equipo`).then(setGente).catch(() => setGente([]))
    api.get<Proyecto[]>(`${ruta}/proyectos`).then(setProyectos).catch(() => setProyectos([]))
  }, [ruta])

  async function enviar() {
    if (!titulo.trim()) {
      setError('La tarea necesita un título.')
      return
    }
    setEnviando(true)
    setError('')
    try {
      const creada = await api.post<Tarea>(`${ruta}/tareas`, {
        titulo,
        descripcion,
        prioridad,
        responsable_id: responsable || null,
        proyecto_id: proyecto ? Number(proyecto) : null,
        vence: vence || null,
      })
      avisar(`Creada ${creada.codigo}.`, 'bien')
      alCrear()
    } catch (e) {
      setError((e as ErrorDeApi).message)
      setEnviando(false)
    }
  }

  return (
    <Dialogo
      titulo="Nueva tarea"
      alCerrar={alCerrar}
      pie={
        <>
          <button className="boton" onClick={alCerrar} disabled={enviando}>Cancelar</button>
          <button className="boton boton--principal" onClick={enviar} disabled={enviando}>
            {enviando ? 'Creando…' : 'Crear'}
          </button>
        </>
      }
    >
      {error && <Error>{error}</Error>}
      <div className="campo">
        <label htmlFor="t">Título</label>
        <input id="t" value={titulo} autoFocus onChange={(e) => setTitulo(e.target.value)} />
      </div>
      <div className="campo">
        <label htmlFor="d">Descripción</label>
        <textarea id="d" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
      </div>
      <div className="campo">
        <label htmlFor="p">Prioridad</label>
        <select id="p" value={prioridad} onChange={(e) => setPrioridad(e.target.value as Prioridad)}>
          {Object.entries(PRIORIDAD).map(([clave, texto]) => (
            <option key={clave} value={clave}>{texto}</option>
          ))}
        </select>
      </div>
      {proyectos.length > 0 && (
        <div className="campo">
          <label htmlFor="pr">Proyecto</label>
          <select id="pr" value={proyecto} onChange={(e) => setProyecto(e.target.value)}>
            <option value="">Suelta, sin proyecto</option>
            {proyectos.map((p) => (
              <option key={p.id} value={p.id}>{p.codigo} · {p.nombre}</option>
            ))}
          </select>
        </div>
      )}
      <div className="campo">
        <label htmlFor="r">Responsable</label>
        <select id="r" value={responsable} onChange={(e) => setResponsable(e.target.value)}>
          <option value="">Sin asignar</option>
          {gente.map((m) => (
            <option key={m.id} value={m.persona.id}>{m.persona.nombre}</option>
          ))}
        </select>
      </div>
      <div className="campo">
        <label htmlFor="v">Fecha límite</label>
        <input id="v" type="date" value={vence} onChange={(e) => setVence(e.target.value)} />
      </div>
    </Dialogo>
  )
}
